import { Card, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import { ATACProjectStatus, Project } from '@app/shared/types/project.type';
import { getFirstDimensionId } from 'helpers/utils';

interface ProjectCardProps {
  project: Project;
  basePath?: string;
}

const ATAC_STATUS_LABELS: Record<ATACProjectStatus, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  assessed: 'Assessed',
  certified: 'Certified'
};

const atacStatusBadgeClass = (status: ATACProjectStatus) => {
  switch (status) {
    case 'draft':
      return 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle';
    case 'in_progress':
      return 'bg-primary-subtle text-primary border-primary-subtle';
    case 'assessed':
      return 'bg-warning-subtle text-warning-emphasis border-warning-subtle';
    case 'certified':
      return 'bg-success-subtle text-success border-success-subtle';
    default:
      return 'bg-secondary-subtle text-secondary-emphasis border-secondary-subtle';
  }
};

const formatCreatedDate = (dateStr?: string) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
};

const ProjectCard = ({ project, basePath = '/project' }: ProjectCardProps) => {
  const { title, projectId, domain, overview, atacStatus, provider, modelName, createdOn } = project;
  const navigate = useNavigate();
  const isATEPlus = basePath === '/ate+';

  const handleCardClick = () => {
    localStorage.setItem('projectId', projectId);
    if (basePath === '/csa+') {
      navigate(`/csa+/project-details/${encodeURIComponent(projectId)}`);
    } else if (basePath === '/mtc+') {
      navigate(`/mtc+/project-details/${encodeURIComponent(projectId)}`);
    } else if (basePath === '/ate+') {
      navigate(`/ate+/project/${encodeURIComponent(projectId)}`);
    } else {
      navigate(
        `${basePath}/details-tabs?dimension-id=${getFirstDimensionId()}&projectId=${encodeURIComponent(projectId)}`
      );
    }
  };

  return (
    <Col xs={12} sm={6} md={3} className="mb-3">
      <Card
        className="shadow-sm h-100 cursor-pointer border-0"
        style={{ minHeight: 65, cursor: 'pointer' }}
        onClick={handleCardClick}
      >
        <Card.Body className="d-flex flex-column py-4 px-4">
          <div className="d-flex justify-content-between align-items-start mb-1 gap-1">
            <span className="fw-bold text-body-emphasis flex-grow-1" style={{ lineHeight: 1.3, fontSize: '1rem' }}>
              {title}
            </span>
            <RevealDropdownTrigger className="flex-shrink-0" onClick={e => e.stopPropagation()}>
              <RevealDropdown>
                <ActionDropdownItems project={project} basePath={basePath} />
              </RevealDropdown>
            </RevealDropdownTrigger>
          </div>

          {isATEPlus && atacStatus && (
            <div className="mb-2">
              <span className={`badge border ${atacStatusBadgeClass(atacStatus)}`}>
                {ATAC_STATUS_LABELS[atacStatus]}
              </span>
            </div>
          )}

          {isATEPlus && provider && (
            <div className="text-body-secondary mb-1" style={{ fontSize: '0.85rem' }}>
              Provider: {provider}
            </div>
          )}

          {isATEPlus && modelName && (
            <div className="text-body-secondary mb-1" style={{ fontSize: '0.85rem' }}>
              Model: {modelName}
            </div>
          )}

          {!isATEPlus && domain && (
            <div className="d-flex align-items-center gap-1 mb-1 text-body-secondary" style={{ fontSize: '0.9rem' }}>
              <FeatherIcon icon="briefcase" size={14} />
              <span>{domain}</span>
            </div>
          )}

          {isATEPlus && createdOn && (
            <div className="text-muted mt-auto" style={{ fontSize: '0.8rem' }}>
              Created: {formatCreatedDate(createdOn)}
            </div>
          )}

          {!isATEPlus && overview && (
            <p className="text-body-secondary mb-0 mt-auto flex-grow-1" style={{ fontSize: '0.9rem', lineHeight: 1.3 }}>
              {overview.length > 60 ? `${overview.slice(0, 60)}...` : overview}
            </p>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default ProjectCard;
